import { fromDate, getLocalTimeZone, toCalendarDate } from '@internationalized/date';
import { clsx } from 'clsx';
import { CSSProperties, FocusEvent, forwardRef, Ref, useState } from 'react';
import {
  DatePicker as AriaDatePicker,
  Text as AriaText,
  ButtonContext,
  DateInput,
  DateSegment,
  DateValue,
  FieldError,
  Group,
  Input,
  Label,
} from 'react-aria-components';
import IconButton from '~/components/buttons/NewIconButton';
import CalendarPopover from '~/components/inputs/DatePicker/CalendarPopover';
import fieldStyles from '~/components/inputs/Field.module.scss';
import CalendarIcon from '~/icons/icon-calendar.svg?react';
import IconClose from '~/icons/icon-close.svg?react';
import { normalizeYear } from '~/utils/datepicker/normalize-year';
import { formatDateForDisplay } from '~/utils/formatDate';
import styles from './DatePicker.module.scss';

interface PresetDate {
  date: Date;
  id: string;
  label: string;
}

export interface DatePickerProps {
  className?: string;
  errorMessage?: string;
  helpText?: string;
  hideLabel?: boolean;
  hidePlaceholder?: boolean;
  id?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  isRequired?: boolean;
  label: string;
  maxValue?: Date | undefined;
  minValue?: Date | undefined;
  name?: string;
  onBlur?: (e: FocusEvent<Element, Element>) => void;
  onChange: (value: Date) => void;
  presetDates?: PresetDate[];
  size?: 'small' | 'medium' | 'large';
  style?: CSSProperties;
  value: Date | null;
  disableTextEditor?: boolean;
  placeholder?: string;
  disableWeekends?: boolean;
  hideIconButton?: boolean;
  clearable?: boolean;
  onClear?: () => void;
}

const DatePicker = forwardRef(
  (
    {
      className,
      errorMessage,
      helpText,
      hideLabel,
      hidePlaceholder,
      id,
      isDisabled,
      isInvalid,
      isRequired,
      label,
      maxValue,
      minValue,
      name,
      onBlur,
      onChange,
      presetDates,
      size = 'medium',
      style,
      value,
      disableTextEditor,
      placeholder,
      disableWeekends = false,
      hideIconButton = false,
      clearable = false,
      onClear,
    }: DatePickerProps,
    ref?: Ref<HTMLDivElement>,
  ) => {
    const [isOpen, setIsOpen] = useState<boolean>();
    const handleChange = (value: DateValue | null) => {
      if (value) {
        const date = value.toDate(getLocalTimeZone());
        onChange(date);
      }
    };

    const handleBlur = (e: FocusEvent<Element, Element>) => {
      if (!value && onBlur) onBlur(e);
      if (!value) return;
      const calendarDate = toCalendarDate(fromDate(value, getLocalTimeZone()));
      const normalizedDate = calendarDate.set({ year: normalizeYear(calendarDate.year) });

      handleChange(normalizedDate);

      if (onBlur) {
        onBlur(e);
      }
    };

    const isDateUnavailable = disableWeekends
      ? (date: DateValue) => {
          const day = date.toDate(getLocalTimeZone()).getDay();
          return day === 0 || day === 6;
        }
      : undefined;

    const parsedValue = value ? toCalendarDate(fromDate(value, getLocalTimeZone())) : clearable ? null : undefined;
    const parsedMaxValue = maxValue ? toCalendarDate(fromDate(maxValue, getLocalTimeZone())) : undefined;
    const parsedMinValue = minValue ? toCalendarDate(fromDate(minValue, getLocalTimeZone())) : undefined;

    const handlePresetDate = (date: Date) => {
      onChange(date);
      setIsOpen(false);
    };
    return (
      <AriaDatePicker
        aria-label={label}
        className={clsx(fieldStyles.field, styles.datePickerField, className)}
        data-size={size}
        id={id}
        isDisabled={isDisabled}
        isInvalid={isInvalid || !!errorMessage}
        isOpen={isOpen}
        isRequired={isRequired}
        isDateUnavailable={isDateUnavailable}
        maxValue={parsedMaxValue}
        minValue={parsedMinValue}
        name={name}
        onOpenChange={setIsOpen}
        onBlur={handleBlur}
        onChange={handleChange}
        ref={ref}
        style={style}
        validationBehavior="aria"
        value={parsedValue}
      >
        {!hideLabel && (
          <Label>
            {label} {isRequired ? ' *' : null}
          </Label>
        )}
        {disableTextEditor ? (
          <Group
            onClick={() => !isDisabled && setIsOpen(!isOpen)}
            className={clsx(styles.inputWrapper, styles.selectOnly)}
          >
            <Input
              className={styles.input}
              readOnly
              value={value ? formatDateForDisplay(value) : ''}
              placeholder={placeholder}
            />
            <div className={styles.iconButtons}>
              {!hideIconButton && (
                <IconButton
                  className={styles.control}
                  Icon={CalendarIcon}
                  disabled={isDisabled}
                  variant="ghost"
                  size={size === 'large' ? 'medium' : 'small'}
                />
              )}
              {clearable && !!value && (
                <ButtonContext.Provider value={null}>
                  <IconButton
                    className={styles.clear}
                    Icon={IconClose}
                    disabled={isDisabled}
                    variant="ghost"
                    size={size === 'large' ? 'medium' : 'small'}
                    onClick={() => onClear?.()}
                  />
                </ButtonContext.Provider>
              )}
            </div>
          </Group>
        ) : (
          <Group className={styles.inputWrapper}>
            <DateInput className={styles.input}>
              {(segment) => (
                <DateSegment
                  className={clsx(styles.segment, { [styles.hideSegment!]: hidePlaceholder && !value })}
                  segment={segment}
                />
              )}
            </DateInput>
            <div className={styles.iconButtons}>
              {!hideIconButton && (
                <IconButton
                  className={styles.control}
                  Icon={CalendarIcon}
                  disabled={isDisabled}
                  variant="ghost"
                  size={size === 'large' ? 'medium' : 'small'}
                />
              )}
              {clearable && !!value && (
                <ButtonContext.Provider value={null}>
                  <IconButton
                    className={styles.clear}
                    Icon={IconClose}
                    disabled={isDisabled}
                    variant="ghost"
                    size={size === 'large' ? 'medium' : 'small'}
                    onClick={() => onClear?.()}
                  />
                </ButtonContext.Provider>
              )}
            </div>
          </Group>
        )}
        {helpText && <AriaText slot="description">{helpText}</AriaText>}
        <FieldError>{errorMessage}</FieldError>
        <CalendarPopover
          isDateUnavailable={isDateUnavailable}
          onSelectPreset={handlePresetDate}
          presetDates={presetDates}
        />
      </AriaDatePicker>
    );
  },
);

export default DatePicker;
